/* eslint-disable */
/**
 * TopBjaelke — v1.7.6
 *
 * Endelig struktur: Overblik · Sager · Hjemmeside · Hjerne · Økonomi · Agenter · Afdelinger · Analytics · Scheduler · Mere ▾ + Chat-ikon + René/Log ud
 *
 * Mere ▾ giver adgang til alle faner fra NY_NAV_GROUPS der ikke
 * allerede er synlige i topbaren — dvs. alle statistik-dashboards:
 *   FORRETNING extra, SAGS-DIAGNOSTIK, INVESTERING & VÆKST,
 *   ANALYSE EXTRA, DATA SCIENCE, BUSINESS INTELLIGENCE,
 *   STRATEGISK RAADGIVER, OS SYSTEM extra, ARBEJDSPLADS, UDAD extra
 */

import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Home, ChevronDown } from 'lucide-react';

// OS-17: Titel per sektion — vises i venstre side ved aktivt panel
const SEKTION_TITLER = {
  oversigt:                  'Overblik',
  firma:                     'Firma',
  okonomi:                   'Økonomi',
  bi:                        'BI Dashboard',
  sager:                     'Sager',
  os_oversigt:               'OS Oversigt',
  hjerne:                    'Hjerne',
  vaekst:                    'Vækst',
  afdelinger:                'Afdelinger',
  orchestrator:              'Orchestrator',
  indbakke:                  'Indbakke',
  ideer_projekter:           'Idéer & Projekter',
  teamnoter:                 'Teamnotater',
  chat:                      'Opgave Chat',
  chief:                     'Chief of Staff',
  hjemmeside:                'Hjemmeside',
  analytics:                 'Analytics',
  scheduler:                 'Scheduler',
  infoskaerm:                'Infoskærm',
  agenter:                   'Agenter',
  beregner_data:             'Beregner-data',
  stagnationsradar:          'Stagnationsradar',
  deadline_kommandocenter:   'Deadline-center',
  aktivitetsscore:           'Aktivitetsscore',
  sagsbehandler_workload:    'Workload',
  fuldmagt_compliance:       'Fuldmagt',
  iv_revenue_velocity:       'Revenue Velocity',
  iv_klientvaekst_scenarie:  'Klientvækst & Scenarier',
  iv_scenarie_simulator:     'Scenarie-Simulator',
  iv_churn_reaktivering:     'Churn & Re-aktivering',
  iv_beregner_heatmap:       'Beregner Heatmap',
  a_fasefunnel:              'Fasefunnel',
  a_beregner_pipeline:       'Beregner Pipeline',
  ds_mrr_forecast:           'MRR Forecast',
  ds_beregner_segmentering:  'Beregner-segment',
  ds_anomali_detektion:      'Anomalidetektion',
  ds_sagstid_prognose:       'Sagstidsprognose',
  bi_mrr:                    'MRR-kurve',
  bi_funnel:                 'Sag-Funnel',
  bi_cohort:                 'Cohort Retention',
  bi_heatmap:                'Aktivitetsheatmap',
  sr_mrr:                    'MRR og Scenarier',
  sr_pipeline:               'Klient Pipeline',
  sr_finansiel:              'Finansiel Sundhed',
  sr_seo:                    'SEO-Momentum',
  sr_sagstid:                'Sagstids-Trend',
};

const ACCENT   = '#3fd4c7';
const BG_BAR   = '#070c1b';
const KANT_BAR = 'rgba(63,212,199,0.10)';
const TEKST    = '#e2e8f0';
const SUBTEKST = '#7a8fa6';
const DANGER   = '#ef4444';

// ── Primære genveje (altid synlige i topbaren) ───────────────
const PRIMAERE = [
  { id: 'oversigt',   label: 'Overblik' },
  { id: 'sager',      label: 'Sager' },
  { id: 'hjemmeside', label: 'Hjemmeside' },
  { id: 'hjerne',     label: 'Hjerne' },
  { id: 'chat',       label: 'Chat' },
  { id: 'okonomi',    label: 'Økonomi' },
  { id: 'agenter',    label: 'Agenter' },
  { id: 'afdelinger', label: 'Afdelinger' },
  { id: 'analytics',  label: 'Analytics' },
  { id: 'scheduler',  label: 'Scheduler' },
];

// Set af primære fane-id'er — bruges til at filtrere dem ud af dropdown
const PRIMAERE_IDS = new Set(PRIMAERE.map(p => p.id));

// ── Faner til "Mere"-dropdown (kategoriseret) ────────────────
// Alle faner fra NY_NAV_GROUPS der IKKE er i PRIMAERE_IDS
const MERE_GRUPPER = [
  {
    label: 'FORRETNING',
    items: [
      { id: 'firma',         label: 'Firma' },
      { id: 'bi',            label: 'BI Dashboard' },
      { id: 'beregner_data', label: 'Beregner-data' },
    ],
  },
  {
    label: 'SAGS-DIAGNOSTIK',
    items: [
      { id: 'stagnationsradar',        label: 'Stagnationsradar' },
      { id: 'deadline_kommandocenter', label: 'Deadline-center' },
      { id: 'aktivitetsscore',         label: 'Aktivitetsscore' },
      { id: 'sagsbehandler_workload',  label: 'Workload' },
      { id: 'fuldmagt_compliance',     label: 'Fuldmagt' },
    ],
  },
  {
    label: 'INVESTERING & VÆKST',
    items: [
      { id: 'iv_revenue_velocity',      label: 'Revenue Velocity' },
      { id: 'iv_klientvaekst_scenarie', label: 'Klientvækst & Scenarier' },
      { id: 'iv_scenarie_simulator',    label: 'Scenarie-Simulator' },
      { id: 'iv_churn_reaktivering',    label: 'Churn & Re-aktivering' },
      { id: 'iv_beregner_heatmap',      label: 'Beregner Heatmap' },
    ],
  },
  {
    label: 'ANALYSE EXTRA',
    items: [
      { id: 'a_fasefunnel',        label: 'Fasefunnel' },
      { id: 'a_beregner_pipeline', label: 'Beregner Pipeline' },
    ],
  },
  {
    label: 'DATA SCIENCE',
    items: [
      { id: 'ds_mrr_forecast',          label: 'MRR Forecast' },
      { id: 'ds_beregner_segmentering', label: 'Beregner-segment' },
      { id: 'ds_anomali_detektion',     label: 'Anomalidetektion' },
      { id: 'ds_sagstid_prognose',      label: 'Sagstidsprognose' },
    ],
  },
  {
    label: 'BUSINESS INTELLIGENCE',
    items: [
      { id: 'bi_mrr',     label: 'MRR-kurve' },
      { id: 'bi_funnel',  label: 'Sag-Funnel' },
      { id: 'bi_cohort',  label: 'Cohort Retention' },
      { id: 'bi_heatmap', label: 'Aktivitetsheatmap' },
    ],
  },
  {
    label: 'STRATEGISK RAADGIVER',
    items: [
      { id: 'sr_mrr',       label: 'MRR og Scenarier' },
      { id: 'sr_pipeline',  label: 'Klient Pipeline' },
      { id: 'sr_finansiel', label: 'Finansiel Sundhed' },
      { id: 'sr_seo',       label: 'SEO-Momentum' },
      { id: 'sr_sagstid',   label: 'Sagstids-Trend' },
    ],
  },
  {
    label: 'OS SYSTEM',
    items: [
      { id: 'os_oversigt',  label: 'OS Oversigt' },
      { id: 'vaekst',       label: 'Vækst' },
      { id: 'orchestrator', label: 'Orchestrator' },
    ],
  },
  {
    label: 'ARBEJDSPLADS',
    items: [
      { id: 'indbakke',        label: 'Indbakke' },
      { id: 'ideer_projekter', label: 'Idéer & Projekter' },
      { id: 'teamnoter',       label: 'Teamnotater' },
      { id: 'chief',           label: 'Chief of Staff' },
    ],
  },
  {
    label: 'UDAD',
    items: [
      { id: 'infoskaerm', label: 'Infoskærm' },
    ],
  },
];

// ── Genvej-knap ───────────────────────────────────────────────
function NavGenvej({ id, label, aktiv, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={() => onClick(id)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background:    'none',
        border:        'none',
        cursor:        'pointer',
        padding:       '0 16px',
        height:        48,
        fontSize:      14,
        fontWeight:    aktiv ? 700 : 500,
        color:         aktiv ? ACCENT : hover ? '#c8d8ee' : SUBTEKST,
        borderBottom:  aktiv
          ? `2px solid ${ACCENT}`
          : hover
          ? '2px solid rgba(63,212,199,0.3)'
          : '2px solid transparent',
        borderTop:     '2px solid transparent',
        boxSizing:     'border-box',
        transition:    'color 0.15s',
        letterSpacing: 0.2,
        flexShrink:    0,
      }}
    >
      {label}
    </button>
  );
}

// ── Mere-dropdown ─────────────────────────────────────────────
function MereDropdown({ aktivSektion, onNavigere }) {
  const [aaben, setAaben]   = useState(false);
  const [hover, setHover]   = useState(false);
  const ref                  = useRef(null);

  // Er nogen af dropdown-fanerne aktive?
  const dropdownAktiv = MERE_GRUPPER.some(g =>
    g.items.some(item => item.id === aktivSektion)
  );

  // Luk ved klik udenfor
  useEffect(() => {
    if (!aaben) return;
    function handleUdenfor(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setAaben(false);
      }
    }
    document.addEventListener('mousedown', handleUdenfor);
    return () => document.removeEventListener('mousedown', handleUdenfor);
  }, [aaben]);

  const handleVaelg = (id) => {
    onNavigere(id);
    setAaben(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      {/* Knap */}
      <button
        onClick={() => setAaben(v => !v)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          background:    'none',
          border:        'none',
          cursor:        'pointer',
          padding:       '0 14px',
          height:        48,
          fontSize:      14,
          fontWeight:    (aaben || dropdownAktiv) ? 700 : 500,
          color:         (aaben || dropdownAktiv) ? ACCENT : hover ? '#c8d8ee' : SUBTEKST,
          borderBottom:  (aaben || dropdownAktiv)
            ? `2px solid ${ACCENT}`
            : hover
            ? '2px solid rgba(63,212,199,0.3)'
            : '2px solid transparent',
          borderTop:     '2px solid transparent',
          boxSizing:     'border-box',
          transition:    'color 0.15s',
          letterSpacing: 0.2,
          display:       'flex',
          alignItems:    'center',
          gap:           5,
        }}
      >
        Mere
        <ChevronDown
          size={13}
          style={{
            transition: 'transform 0.2s',
            transform:  aaben ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>

      {/* Dropdown-panel */}
      {aaben && (
        <div style={{
          position:        'absolute',
          top:             50,
          right:           0,
          width:           680,
          maxHeight:       'calc(100vh - 80px)',
          overflowY:       'auto',
          background:      '#0b1120',
          border:          '1px solid rgba(63,212,199,0.15)',
          borderRadius:    10,
          boxShadow:       '0 8px 32px rgba(0,0,0,0.6)',
          zIndex:          300,
          padding:         '12px 0',
        }}>
          {/* Grid med 2 kolonner af grupper */}
          <div style={{
            display:             'grid',
            gridTemplateColumns: '1fr 1fr',
            gap:                 0,
            padding:             '0 4px',
          }}>
            {MERE_GRUPPER.map((gruppe) => (
              <div key={gruppe.label} style={{ padding: '8px 12px 12px' }}>
                {/* Gruppe-header */}
                <div style={{
                  fontSize:      10,
                  fontWeight:    700,
                  color:         'rgba(63,212,199,0.5)',
                  letterSpacing: '0.1em',
                  marginBottom:  6,
                  paddingBottom: 4,
                  borderBottom:  '1px solid rgba(255,255,255,0.05)',
                  textTransform: 'uppercase',
                }}>
                  {gruppe.label}
                </div>
                {/* Faner */}
                {gruppe.items.map((item) => (
                  <DropdownFaneKnap
                    key={item.id}
                    id={item.id}
                    label={item.label}
                    aktiv={aktivSektion === item.id}
                    onClick={handleVaelg}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Enkelt fane-knap inde i dropdown ─────────────────────────
function DropdownFaneKnap({ id, label, aktiv, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={() => onClick(id)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display:       'block',
        width:         '100%',
        textAlign:     'left',
        background:    aktiv
          ? 'rgba(63,212,199,0.12)'
          : hover
          ? 'rgba(255,255,255,0.05)'
          : 'none',
        border:        'none',
        borderRadius:  5,
        cursor:        'pointer',
        padding:       '6px 10px',
        fontSize:      13,
        fontWeight:    aktiv ? 600 : 400,
        color:         aktiv ? ACCENT : hover ? '#c8d8ee' : '#9ab0cc',
        transition:    'background 0.1s, color 0.1s',
        letterSpacing: 0.1,
        marginBottom:  1,
      }}
    >
      {aktiv && (
        <span style={{ marginRight: 6, fontSize: 10, color: ACCENT }}>●</span>
      )}
      {label}
    </button>
  );
}

// ── TopBjaelke — hoved-eksport ────────────────────────────────
export default function TopBjaelke({
  aktivSektion,
  onNavigere,
  onLogout,
  onChatAabn,
  onChatÅbn,
  onTilbage,
  serverVersion,
  ulæstIndbakke,
}) {
  const [logudHover,      setLogudHover]      = useState(false);
  const [chatHover,       setChatHover]       = useState(false);
  const [sagsportalHover, setSagsportalHover] = useState(false);

  // Chat-ikon navigerer til chat-panel (fuldt panel) — overlay er fjernet
  const handleChatAabn = () => onNavigere && onNavigere('chat');

  return (
    <div style={{
      position:             'fixed',
      top:                  0,
      left:                 0,
      right:                0,
      height:               48,
      zIndex:               200,
      background:           BG_BAR,
      borderBottom:         `1px solid ${KANT_BAR}`,
      display:              'flex',
      alignItems:           'center',
      boxSizing:            'border-box',
      backdropFilter:       'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
    }}>

      {/* ── Venstre: Logo + version + aktiv sektion (OS-17) ── */}
      <button
        onClick={() => onNavigere('oversigt')}
        title="Tilbage til Overblik"
        style={{
          background: 'none',
          border:     'none',
          cursor:     'pointer',
          display:    'flex',
          alignItems: 'center',
          gap:        10,
          padding:    '0 20px',
          height:     48,
          flexShrink: 0,
        }}
      >
        <span style={{
          fontSize:      14,
          fontWeight:    800,
          color:         '#f1f5f9',
          letterSpacing: -0.3,
          lineHeight:    1,
        }}>
          SV OS
        </span>
        {serverVersion && (
          <span style={{
            fontSize:      9,
            fontWeight:    700,
            background:    'rgba(201,168,76,0.15)',
            border:        '1px solid rgba(201,168,76,0.4)',
            borderRadius:  4,
            padding:       '2px 6px',
            color:         '#c9a84c',
            fontFamily:    'monospace',
            letterSpacing: '0.03em',
          }}>
            v{serverVersion}
          </span>
        )}
        {/* OS-17: Vis aktiv fanes titel til højre for logo — opdateres ved skift */}
        {aktivSektion && aktivSektion !== 'oversigt' && SEKTION_TITLER[aktivSektion] && (
          <>
            <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: 14 }}>›</span>
            <span style={{
              fontSize:   13,
              fontWeight: 600,
              color:      ACCENT,
              letterSpacing: 0.1,
            }}>
              {SEKTION_TITLER[aktivSektion]}
            </span>
          </>
        )}
      </button>

      {/* ── Centrum: 9 primære genveje + Mere-dropdown ── */}
      <div style={{
        flex:           1,
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        height:         48,
      }}>
        {PRIMAERE.map(genv => (
          <NavGenvej
            key={genv.id}
            id={genv.id}
            label={genv.label}
            aktiv={aktivSektion === genv.id}
            onClick={onNavigere}
          />
        ))}
        <MereDropdown
          aktivSektion={aktivSektion}
          onNavigere={onNavigere}
        />
      </div>

      {/* ── Højre: Sagsportal + Chat + Bruger + Log ud ── */}
      <div style={{
        display:    'flex',
        alignItems: 'center',
        gap:        4,
        padding:    '0 16px',
        flexShrink: 0,
      }}>
        {/* Sagsportal-genvej — tilbage til sagsportalen */}
        {onTilbage && (
          <>
            <button
              onClick={onTilbage}
              onMouseEnter={() => setSagsportalHover(true)}
              onMouseLeave={() => setSagsportalHover(false)}
              title="Tilbage til Sagsportalen"
              aria-label="Tilbage til Sagsportalen"
              style={{
                background:     sagsportalHover ? 'rgba(63,212,199,0.10)' : 'none',
                border:         `1px solid ${sagsportalHover ? 'rgba(63,212,199,0.3)' : 'rgba(255,255,255,0.08)'}`,
                borderRadius:   7,
                cursor:         'pointer',
                height:         36,
                padding:        '0 12px',
                display:        'flex',
                alignItems:     'center',
                gap:            6,
                fontSize:       13,
                fontWeight:     500,
                color:          sagsportalHover ? ACCENT : SUBTEKST,
                transition:     'all 0.15s',
                flexShrink:     0,
                letterSpacing:  0.1,
              }}
            >
              <Home size={15} />
              <span>Sagsportal</span>
            </button>
            {/* Separator */}
            <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.08)', margin: '0 4px' }} />
          </>
        )}

        {/* Chat-ikon — OS-05: min 44×44px tap-target */}
        <button
          onClick={handleChatAabn}
          onMouseEnter={() => setChatHover(true)}
          onMouseLeave={() => setChatHover(false)}
          title="Åbn OS Chat"
          aria-label="Åbn OS Chat"
          style={{
            background:     chatHover ? 'rgba(63,212,199,0.10)' : 'none',
            border:         `1px solid ${chatHover ? 'rgba(63,212,199,0.3)' : 'rgba(255,255,255,0.08)'}`,
            borderRadius:   7,
            cursor:         'pointer',
            width:          44,
            height:         44,
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            fontSize:       16,
            color:          chatHover ? ACCENT : SUBTEKST,
            transition:     'all 0.15s',
            flexShrink:     0,
          }}
        >
          <MessageSquare size={18} />
        </button>

        {/* Separator */}
        <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.08)', margin: '0 4px' }} />

        {/* Brugernavn */}
        <span style={{ fontSize: 13, fontWeight: 500, color: TEKST, marginRight: 4, letterSpacing: 0.1 }}>
          René
        </span>

        {/* Log ud — OS-05: min 44×44px tap-target */}
        <button
          onClick={() => {
            if (onLogout) onLogout();
            else {
              sessionStorage.removeItem('sv_token_exp');
              sessionStorage.removeItem('sv_bruger');
              window.location.reload();
            }
          }}
          onMouseEnter={() => setLogudHover(true)}
          onMouseLeave={() => setLogudHover(false)}
          aria-label="Log ud"
          style={{
            background:    'none',
            border:        `1px solid ${logudHover ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.08)'}`,
            cursor:        'pointer',
            fontSize:      12,
            color:         logudHover ? DANGER : SUBTEKST,
            minWidth:      44,
            minHeight:     44,
            padding:       '0 10px',
            borderRadius:  7,
            transition:    'color 0.15s, border-color 0.15s',
            letterSpacing: 0.1,
            display:       'flex',
            alignItems:    'center',
            justifyContent:'center',
          }}
        >
          Log ud →
        </button>
      </div>
    </div>
  );
}
